import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { legacyWaybillsApi, type PaidOrderItem } from '@/api/legacy';

/**
 * Получить список товаров готовых к выписке накладных
 * @param clientNames - имена клиентов для фильтрации
 * @param includeUserOrders - включить заказы созданные пользователем
 */
export function usePaidOrders(clientNames: string[], includeUserOrders = false) {
  return useQuery({
    queryKey: ['waybills', 'paid-orders', clientNames, includeUserOrders],
    queryFn: () => legacyWaybillsApi.getPaidOrders(clientNames, includeUserOrders),
    enabled: clientNames.length > 0,
    staleTime: 60_000, // 1 минута
  });
}

/**
 * Заказать накладную
 */
export function useOrderWaybill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderId: number) => legacyWaybillsApi.orderWaybill(orderId),
    onMutate: async (orderId: number) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['waybills'] });

      // Snapshot previous values for rollback
      const previousData = queryClient.getQueriesData<PaidOrderItem[]>({ queryKey: ['waybills', 'paid-orders'] });

      // Optimistically remove the item from all waybills queries
      queryClient.setQueriesData<PaidOrderItem[]>(
        { queryKey: ['waybills', 'paid-orders'] },
        (old) => old?.filter((item) => String(item.id) !== String(orderId))
      );

      return { previousData };
    },
    onSuccess: () => {
      toast.success('Заявка создана', {
        description: 'Накладная будет подготовлена',
      });
    },
    onError: (error, _orderId, context) => {
      // Rollback on error
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      const message = error instanceof Error ? error.message : 'Ошибка создания заявки';
      toast.error('Ошибка', { description: message });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['waybills'] });
    },
  });
}

/**
 * Группировка товаров по клиентам для UI
 */
export function groupByClient(items: PaidOrderItem[]): Map<string, PaidOrderItem[]> {
  const map = new Map<string, PaidOrderItem[]>();
  for (const item of items) {
    const existing = map.get(item.company) || [];
    existing.push(item);
    map.set(item.company, existing);
  }
  return map;
}
